import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Save, Loader2, PenTool, CheckCircle2, Plus, Trash2, GitBranch, Copy, Webhook } from "lucide-react";

const PARTNER_SOURCES = [
  ["", "— static / default only —"],
  ["name", "Partner Name"],
  ["firm_name", "Firm / Company Name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["pan", "PAN"],
  ["aadhaar", "Aadhaar"],
  ["gst", "GST"],
  ["business_type", "Business Type"],
  ["address", "Address"],
  ["city", "City"],
  ["state", "State"],
  ["pincode", "Pincode"],
  ["bank_name", "Bank Name"],
  ["account_number", "Account Number"],
  ["ifsc", "IFSC"],
  ["sales_manager_name", "Sales Manager"],
];

export default function Leegality() {
  const [form, setForm] = useState({ auth_token: "", profile_id: "", callback_url: "", environment: "sandbox", active: false, template_fields: [], invitee: { name_source: "name", email_source: "email", phone_source: "phone" } });
  const [tokenSet, setTokenSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/leegality").then((r) => {
      setForm({
        auth_token: "",
        profile_id: r.data.profile_id || "",
        callback_url: r.data.callback_url || "",
        environment: r.data.environment || "sandbox",
        active: r.data.active,
        template_fields: r.data.template_fields || [],
        invitee: r.data.invitee || { name_source: "name", email_source: "email", phone_source: "phone" },
      });
      setTokenSet(r.data.auth_token_set);
    }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/leegality", form);
      setTokenSet(r.data.auth_token_set);
      setForm((f) => ({ ...f, auth_token: "", template_fields: r.data.template_fields || [] }));
      toast.success("Leegality settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const addMap = () => setForm((f) => ({ ...f, template_fields: [...f.template_fields, { field_id: "", leegality_field: "", source: "name", default: "", required: true }] }));
  const updMap = (idx, patch) => setForm((f) => ({ ...f, template_fields: f.template_fields.map((m, i) => i === idx ? { ...m, ...patch } : m) }));
  const delMap = (idx) => setForm((f) => ({ ...f, template_fields: f.template_fields.filter((_, i) => i !== idx) }));

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="leegality-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><PenTool size={24} className="text-emerald-600" /> Leegality e-Sign</h1>
        <p className="text-sm text-slate-500 mt-1">Configure the Leegality Document Execution API used to e-sign partner agreements</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div>
          <label className={labelCls}>Auth Token (X-Auth-Token) {tokenSet && <span className="text-emerald-600 text-xs font-medium ml-1"><CheckCircle2 size={12} className="inline" /> saved</span>}</label>
          <input data-testid="leegality-token" type="password" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} className={inputCls} placeholder={tokenSet ? "•••••••• (leave blank to keep existing)" : "Paste your Leegality auth token"} />
        </div>
        <div>
          <label className={labelCls}>Workflow / Profile ID <span className="text-rose-500 text-xs font-medium ml-1">required by the e-sign API</span></label>
          <input data-testid="leegality-profile" value={form.profile_id} onChange={(e) => setForm({ ...form, profile_id: e.target.value })} className={inputCls} placeholder="Workflow ID from Leegality dashboard (e.g. 7WMVrYf)" />
        </div>
        <div>
          <label className={labelCls}>Environment</label>
          <select data-testid="leegality-env" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} className={inputCls}>
            <option value="sandbox">Sandbox (sandbox.leegality.com)</option>
            <option value="production">Production (app1.leegality.com)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Callback URL <span className="text-slate-400 text-xs font-medium ml-1">optional — Leegality will POST sign events here</span></label>
          <input data-testid="leegality-callback-url" value={form.callback_url} onChange={(e) => setForm({ ...form, callback_url: e.target.value })} className={inputCls} placeholder={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/leegality/webhook`} />
          <p className="text-[11px] text-slate-400 mt-1">Sent as <code className="bg-slate-100 px-1 rounded">callbackUrl</code> on every sign request. Leave blank to skip the callback.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="leegality-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Enable e-signing</label>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-sm p-5" data-testid="webhook-url-section">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0"><Webhook size={20} /></div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">Webhook URL <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">receive from Leegality</span></h2>
            <p className="text-xs text-slate-400 mt-0.5">Paste this URL into your Leegality dashboard under <span className="text-slate-300 font-medium">Webhooks → Add Webhook</span>. Sign, Rejected, Expired and other events will be POSTed here — we auto-fetch and attach the signed PDF on success.</p>
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5">
              <code data-testid="webhook-url-value" className="flex-1 text-sm font-mono text-emerald-200 break-all">{`${(process.env.REACT_APP_BACKEND_URL || "")}/api/leegality/webhook`}</code>
              <button data-testid="copy-webhook-url" type="button" onClick={() => { navigator.clipboard?.writeText(`${(process.env.REACT_APP_BACKEND_URL || "")}/api/leegality/webhook`); toast.success("Webhook URL copied"); }} className="p-2 rounded-lg text-slate-300 hover:text-emerald-300 hover:bg-slate-700"><Copy size={15} /></button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">Method: POST</span>
              <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">Content-Type: application/json</span>
              <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">Auth: public</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3" data-testid="invitee-mapping-section">
        <div>
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><GitBranch size={18} className="text-emerald-600" /> Invitee Mapping</h2>
          <p className="text-xs text-slate-500 mt-0.5">Choose which partner fields become the invitee <code className="bg-slate-100 px-1 rounded">name</code>, <code className="bg-slate-100 px-1 rounded">email</code>, and <code className="bg-slate-100 px-1 rounded">phone</code> on the sign request.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Invitee Name ← </label>
            <select data-testid="invitee-name-source" value={form.invitee.name_source} onChange={(e) => setForm({ ...form, invitee: { ...form.invitee, name_source: e.target.value } })} className={inputCls}>
              {PARTNER_SOURCES.filter(([k]) => k).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Invitee Email ← </label>
            <select data-testid="invitee-email-source" value={form.invitee.email_source} onChange={(e) => setForm({ ...form, invitee: { ...form.invitee, email_source: e.target.value } })} className={inputCls}>
              {PARTNER_SOURCES.filter(([k]) => k).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Invitee Phone ← </label>
            <select data-testid="invitee-phone-source" value={form.invitee.phone_source} onChange={(e) => setForm({ ...form, invitee: { ...form.invitee, phone_source: e.target.value } })} className={inputCls}>
              {PARTNER_SOURCES.filter(([k]) => k).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3" data-testid="template-fields-section">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><GitBranch size={18} className="text-emerald-600" /> Template Field Mapping</h2>
            <p className="text-xs text-slate-500 mt-0.5">Map fields defined in your Leegality template to partner data. Each mapping is sent as <code className="bg-slate-100 px-1 rounded">file.fields[]</code> — with the Leegality field <span className="font-medium">id</span> and <span className="font-medium">name</span> — on the sign request.</p>
          </div>
          <button data-testid="add-template-field" type="button" onClick={addMap} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium inline-flex items-center gap-1"><Plus size={13} /> Add mapping</button>
        </div>
        <div className="grid grid-cols-12 gap-2 px-1 pt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          <div className="col-span-3">Field ID</div>
          <div className="col-span-3">Field Name</div>
          <div className="col-span-3">Source (Partner)</div>
          <div className="col-span-2">Default</div>
          <div className="col-span-1" />
        </div>
        {form.template_fields.length === 0 && <p className="text-xs text-slate-400 px-1 py-3">No mappings yet. Click <span className="font-medium">Add mapping</span> to send template fields to Leegality.</p>}
        {form.template_fields.map((m, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`template-field-row-${i}`}>
            <input data-testid={`tf-id-${i}`} value={m.field_id || ""} onChange={(e) => updMap(i, { field_id: e.target.value })} className={`${inputCls} col-span-3`} placeholder="1790400126453" />
            <input data-testid={`tf-leegality-${i}`} value={m.leegality_field} onChange={(e) => updMap(i, { leegality_field: e.target.value })} className={`${inputCls} col-span-3`} placeholder="Name_firm" />
            <select data-testid={`tf-source-${i}`} value={m.source || ""} onChange={(e) => updMap(i, { source: e.target.value })} className={`${inputCls} col-span-3`}>
              {PARTNER_SOURCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <input data-testid={`tf-default-${i}`} value={m.default || ""} onChange={(e) => updMap(i, { default: e.target.value })} className={`${inputCls} col-span-2`} placeholder="optional" />
            <button data-testid={`tf-delete-${i}`} type="button" onClick={() => delMap(i)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 justify-self-end"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button data-testid="leegality-save" onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Settings</button>
      </div>
    </div>
  );
}
